import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ItemFilters = ({ 
  filters, 
  onFilterChange, 
  onClearFilters, 
  itemCount,
  categories 
}) => {
  const handleFilterChange = (key, value) => {
    onFilterChange({ ...filters, [key]: value });
  };

  const hasActiveFilters = Object.values(filters)?.some(value => 
    value !== '' && value !== 'all' && value !== null
  );

  const stockStatusOptions = [
    { value: 'all', label: 'All Stock Levels' },
    { value: 'in-stock', label: 'In Stock' },
    { value: 'low-stock', label: 'Low Stock' },
    { value: 'out-of-stock', label: 'Out of Stock' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-foreground">Filter Items</h3>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            className="text-muted-foreground hover:text-foreground"
          >
            <Icon name="X" size={14} className="mr-2" />
            Clear Filters
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {/* Search */}
        <div className="relative">
          <Input
            type="text"
            placeholder="Search items..."
            value={filters?.search || ''}
            onChange={(e) => handleFilterChange('search', e?.target?.value)}
            className="pl-10"
          />
          <Icon 
            name="Search" 
            size={16} 
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" 
          />
        </div>

        {/* Category Filter */}
        <div>
          <select
            value={filters?.category || 'all'}
            onChange={(e) => handleFilterChange('category', e?.target?.value)}
            className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="all">All Categories</option>
            {categories?.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>

        {/* Stock Status Filter */}
        <div>
          <select
            value={filters?.stockStatus || 'all'}
            onChange={(e) => handleFilterChange('stockStatus', e?.target?.value)}
            className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            {stockStatusOptions?.map((option) => (
              <option key={option?.value} value={option?.value}>
                {option?.label}
              </option>
            ))}
          </select>
        </div>

        {/* Status Filter */}
        <div>
          <select
            value={filters?.status || 'all'}
            onChange={(e) => handleFilterChange('status', e?.target?.value)}
            className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            {statusOptions?.map((option) => (
              <option key={option?.value} value={option?.value}>
                {option?.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      {/* Price Range */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Input
            type="number"
            placeholder="Min Price"
            value={filters?.minPrice || ''}
            onChange={(e) => handleFilterChange('minPrice', e?.target?.value)}
            min="0"
            step="0.01"
          />
        </div>
        <div>
          <Input
            type="number"
            placeholder="Max Price"
            value={filters?.maxPrice || ''}
            onChange={(e) => handleFilterChange('maxPrice', e?.target?.value)}
            min="0"
            step="0.01"
          />
        </div>
        <div className="flex items-center text-sm text-muted-foreground">
          <Icon name="Package" size={16} className="mr-2" />
          {itemCount} item{itemCount !== 1 ? 's' : ''} found
        </div>
      </div>
      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex flex-wrap gap-2">
            {filters?.search && (
              <div className="flex items-center bg-primary/10 text-primary px-3 py-1 rounded-full text-sm">
                <span>Search: "{filters?.search}"</span>
                <button
                  onClick={() => handleFilterChange('search', '')}
                  className="ml-2 hover:text-primary/70"
                >
                  <Icon name="X" size={12} />
                </button>
              </div>
            )}
            {filters?.category && filters?.category !== 'all' && (
              <div className="flex items-center bg-primary/10 text-primary px-3 py-1 rounded-full text-sm">
                <span>Category: {filters?.category}</span>
                <button
                  onClick={() => handleFilterChange('category', 'all')}
                  className="ml-2 hover:text-primary/70"
                >
                  <Icon name="X" size={12} />
                </button>
              </div>
            )}
            {filters?.stockStatus && filters?.stockStatus !== 'all' && (
              <div className="flex items-center bg-primary/10 text-primary px-3 py-1 rounded-full text-sm">
                <span>Stock: {stockStatusOptions?.find(opt => opt?.value === filters?.stockStatus)?.label}</span>
                <button
                  onClick={() => handleFilterChange('stockStatus', 'all')}
                  className="ml-2 hover:text-primary/70"
                >
                  <Icon name="X" size={12} />
                </button>
              </div>
            )}
            {filters?.status && filters?.status !== 'all' && (
              <div className="flex items-center bg-primary/10 text-primary px-3 py-1 rounded-full text-sm">
                <span>Status: {statusOptions?.find(opt => opt?.value === filters?.status)?.label}</span>
                <button
                  onClick={() => handleFilterChange('status', 'all')}
                  className="ml-2 hover:text-primary/70"
                >
                  <Icon name="X" size={12} />
                </button>
              </div>
            )}
            {(filters?.minPrice || filters?.maxPrice) && (
              <div className="flex items-center bg-primary/10 text-primary px-3 py-1 rounded-full text-sm">
                <span>
                  Price: ${filters?.minPrice || '0'} - ${filters?.maxPrice || '∞'}
                </span>
                <button
                  onClick={() => {
                    handleFilterChange('minPrice', '');
                    handleFilterChange('maxPrice', '');
                  }}
                  className="ml-2 hover:text-primary/70"
                >
                  <Icon name="X" size={12} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ItemFilters;